import Link from '@tiptap/extension-link'
import { mergeAttributes } from '@tiptap/core'

export const JOHKU_CONTENT_LINK_CLASS = 'johku-content-link'

/** Every class token on a link, in order, with duplicates removed. */
export function linkClassTokens(value: unknown): string[] {
  if (value == null) {
    return []
  }
  const tokens: string[] = []
  const seen = new Set<string>()
  for (const token of String(value).split(/\s+/)) {
    if (!token || seen.has(token)) {
      continue
    }
    seen.add(token)
    tokens.push(token)
  }
  return tokens
}

export function normalizeLinkClass(value: unknown): string | null {
  const tokens = linkClassTokens(value)
  return tokens.length ? tokens.join(' ') : null
}

/**
 * Classes already on the link stay as they are.
 * Content links also keep johku-content-link; saving as a URL removes only that class.
 */
export function linkClassAfterEdit(existing: unknown, isContentLink: boolean): string | null {
  const tokens = linkClassTokens(existing).filter((token) => {
    return isContentLink || token !== JOHKU_CONTENT_LINK_CLASS
  })
  if (isContentLink && !tokens.includes(JOHKU_CONTENT_LINK_CLASS)) {
    tokens.push(JOHKU_CONTENT_LINK_CLASS)
  }
  return tokens.length ? tokens.join(' ') : null
}

const JohkuLink = Link.extend({
  name: 'link',

  addOptions() {
    return {
      ...this.parent?.(),
      openOnClick: false,
      HTMLAttributes: {
        rel: 'noopener',
      },
    }
  },

  addAttributes() {
    return {
      ...this.parent?.(),
      // Keep the raw class list. The stock link mark coerces attribute values and
      // the dialog used to replace class with null (or only johku-content-link).
      class: {
        default: null,
        parseHTML: (element: HTMLElement) => normalizeLinkClass(element.getAttribute('class')),
        renderHTML: (attributes: Record<string, unknown>) => {
          const className = normalizeLinkClass(attributes.class)
          if (!className) {
            return {}
          }
          return { class: className }
        },
      },
      johkuModal: {
        default: null,
        parseHTML: (element: HTMLElement) => element.getAttribute('data-johku-modal'),
        renderHTML: (attributes: Record<string, unknown>) => {
          if (!attributes.johkuModal) {
            return {}
          }
          return { 'data-johku-modal': attributes.johkuModal as string }
        },
      },
      johkuModalTitle: {
        default: null,
        parseHTML: (element: HTMLElement) => element.getAttribute('data-johku-modal-title'),
        renderHTML: (attributes: Record<string, unknown>) => {
          if (!attributes.johkuModalTitle) {
            return {}
          }
          return { 'data-johku-modal-title': attributes.johkuModalTitle as string }
        },
      },
    }
  },

  renderHTML({ HTMLAttributes }) {
    const attrs = { ...HTMLAttributes }
    if (attrs['data-johku-modal']) {
      attrs.class = linkClassAfterEdit(attrs.class, true)
      attrs.href = attrs.href || '#'
    }
    return ['a', mergeAttributes(this.options.HTMLAttributes, attrs), 0]
  },
})

export default JohkuLink
